import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

interface LibraryProps {
  userId: string;
  userName: string;
  onBack: () => void;
}

interface MediaItem {
  id: number;
  title: string;
  thumbnail: string;
  description: string;
  price: number;
  points: number;
  type: "book" | "video" | "live";
}

const Library: React.FC<LibraryProps> = ({ userId, userName, onBack }) => {
  const [view, setView] = useState<"books" | "videos" | "live">("videos");
  const [theme, setTheme] = useState<"dark" | "white" | "pink">("dark");
  const [books, setBooks] = useState<MediaItem[]>([]);
  const [videos, setVideos] = useState<MediaItem[]>([]);
  const [live, setLive] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedItem, setSelectedItem] = useState<MediaItem | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        const booksData: MediaItem[] = [
          {
            id: 1,
            title: "The Adventure Book",
            thumbnail: "https://images.unsplash.com/photo-1544947950-fa07a98d237f?q=80&w=400",
            description: "An exciting journey through a magical world.",
            price: 99,
            points: 10,
            type: "book",
          },
          {
            id: 2,
            title: "Science for Kids",
            thumbnail: "https://images.unsplash.com/photo-1588595150305-38344c8ddf28?q=80&w=400",
            description: "Learn science with fun experiments and facts.",
            price: 99,
            points: 10,
            type: "book",
          },
          {
            id: 3,
            title: "Mystery Tales",
            thumbnail: "https://images.unsplash.com/photo-1512820790803-83ca734da794?q=80&w=400",
            description: "Solve mysteries with young detectives.",
            price: 99,
            points: 10,
            type: "book",
          },
        ];

        const videosData: MediaItem[] = [
          {
            id: 1,
            title: "Space Exploration",
            thumbnail: "https://images.unsplash.com/photo-1446941611757-91d2c3bd3d45?q=80&w=400",
            description: "Discover the wonders of the universe.",
            price: 49,
            points: 5,
            type: "video",
          },
          {
            id: 2,
            title: "Animal Adventures",
            thumbnail: "https://images.unsplash.com/photo-1518791841217-8f162f1e1131?q=80&w=400",
            description: "Explore the animal kingdom with fun stories.",
            price: 49,
            points: 5,
            type: "video",
          },
          {
            id: 3,
            title: "Coding for Beginners",
            thumbnail: "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?q=80&w=400",
            description: "Learn to code with interactive tutorials.",
            price: 49,
            points: 5,
            type: "video",
          },
        ];

        const liveData: MediaItem[] = [
          {
            id: 1,
            title: "Live Science Workshop",
            thumbnail: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?q=80&w=400",
            description: "Join a live interactive science experiment session.",
            price: 199,
            points: 20,
            type: "live",
          },
          {
            id: 2,
            title: "Storytelling with Authors",
            thumbnail: "https://images.unsplash.com/photo-1516979187457-6376b7a1ec1e?q=80&w=400",
            description: "Listen to live storytelling by famous authors.",
            price: 199,
            points: 20,
            type: "live",
          },
          {
            id: 3,
            title: "Kids' Coding Live",
            thumbnail: "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?q=80&w=400",
            description: "Learn coding in a live interactive class.",
            price: 199,
            points: 20,
            type: "live",
          },
        ];

        setBooks(booksData);
        setVideos(videosData);
        setLive(liveData);
        setLoading(false);
      } catch (err) {
        setError("Failed to load library data");
        setLoading(false);
        toast.error("Error loading library data");
      }
    };

    fetchData();
  }, []);

  const handleItemClick = (item: MediaItem, type: "books" | "videos" | "live") => {
    setSelectedItem(item);
    setIsPlaying(false);
    toast.info(`Opened ${type}: ${item.title}`);
  };

  const handleCloseModal = () => {
    setSelectedItem(null);
    setIsPlaying(false);
  };

  const handleBuyNow = (item: MediaItem) => {
    toast.success(`Proceeding to purchase ${item.title} for ₹${item.price}`);
  };

  const handlePlayVideo = () => {
    setIsPlaying(true);
    toast.info(`Playing video: ${selectedItem?.title}`);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    toast.success("Logged out successfully");
    navigate("/login");
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const toggleTheme = () => {
    setTheme((prev) => (prev === "dark" ? "white" : prev === "white" ? "pink" : "dark"));
    toast.info(`Switched to ${theme === "dark" ? "white" : theme === "white" ? "pink" : "dark"} theme`);
  };

  const filteredItems = (items: MediaItem[]) =>
    items.filter((item) =>
      item.title.toLowerCase().includes(searchQuery.toLowerCase())
    );

  const items: MediaItem[] =
    view === "videos" ? filteredItems(videos) :
    view === "books" ? filteredItems(books) :
    view === "live" ? filteredItems(live) : [];

  if (loading) {
    return (
      <div className={`library-container theme-${theme}`}>
        <style>{styles}</style>
        <div className="skeleton-loader">
          {Array(6)
            .fill(0)
            .map((_, index) => (
              <div key={index} className="skeleton-item"></div>
            ))}
        </div>
        <ToastContainer />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`library-container theme-${theme}`}>
        <style>{styles}</style>
        <div className="error-message">{error}</div>
        <ToastContainer />
      </div>
    );
  }

  return (
    <div className={`library-container theme-${theme}`}>
      <style>{styles}</style>
      <div className="library-header">
        <div className="header-content">
          <h1 className="library-title">{userName}'s Library</h1>
        </div>
        <div className="switch-container">
          <button
            className={`switch-button ${view === "videos" ? "selected" : ""}`}
            onClick={() => setView("videos")}
            aria-label="View videos"
          >
            Videos
          </button>
          <button
            className={`switch-button ${view === "books" ? "selected" : ""}`}
            onClick={() => setView("books")}
            aria-label="View books"
          >
            Books
          </button>
          <button
            className={`switch-button ${view === "live" ? "selected" : ""}`}
            onClick={() => setView("live")}
            aria-label="View live content"
          >
            Live
          </button>
        </div>
      </div>
      <div className="age-filter-card">
        <p className="age-filter-text">ℹ️ Top Quality Content for your Age group</p>
      </div>
      <div className="library-grid">
        {items.map((item) => (
          <div
            key={item.id}
            className="library-item"
            onClick={() => handleItemClick(item, view)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && handleItemClick(item, view)}
          >
            <img src={item.thumbnail} alt={item.title} className="thumbnail" />
            <div className="item-content">
              <h3 className="item-title">{item.title}</h3>
              <p className="item-description">{item.description}</p>
              <p className="item-price">₹{item.price}</p>
              <p className="item-points">{item.points} Points</p>
            </div>
          </div>
        ))}
      </div>
      {selectedItem && (
        <div className="modal-overlay">
          <div className="modal-content">
            <button className="modal-close" onClick={handleCloseModal} aria-label="Close modal">
              ×
            </button>
            {isPlaying && selectedItem.type === "video" ? (
              <iframe
                className="modal-video"
                src="https://www.youtube.com/embed/3ZFgt89pUeE?autoplay=1"
                title={selectedItem.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            ) : (
              <img src={selectedItem.thumbnail} alt={selectedItem.title} className="modal-thumbnail" />
            )}
            <h2 className="modal-title">{selectedItem.title}</h2>
            <p className="modal-description">{selectedItem.description}</p>
            <p className="modal-price">Price: ₹{selectedItem.price}</p>
            <p className="modal-points">Points: {selectedItem.points}</p>
            <div className="modal-actions">
              {selectedItem.type === "video" && !isPlaying && (
                <button className="modal-play" onClick={handlePlayVideo} aria-label="Play video">
                  Play
                </button>
              )}
              <button className="modal-action" onClick={() => handleBuyNow(selectedItem)}>
                Buy Now
              </button>
            </div>
          </div>
        </div>
      )}
      <ToastContainer />
    </div>
  );
};

const styles = `
  :root {
    --cyber-green: #00e676;
    --cosmic-pink: #e91e63;
    --midnight-black: #141414;
    --glass-bg: rgba(20, 20, 20, 0.5);
    --card-blur: 8px;
    --background-color: var(--midnight-black);
    --text-color: #ffffff;
    --card-border-color: rgba(255, 255, 255, 0.2);
    --card-text-color: #ffffff;
    --card-bg: rgba(30, 30, 30, 0.95);
    --input-bg: #333333;
    --input-text: #ffffff;
    --input-border: #555555;
    --gray-text: #b3b3b3;
    --gray-text-secondary: #8c8c8c;
  }

  .theme-white {
    --background-color: #f5f5f5;
    --text-color: #141414;
    --card-border-color: #d1d5db;
    --card-text-color: #141414;
    --card-bg: rgba(255, 255, 255, 0.95);
    --glass-bg: rgba(255, 255, 255, 0.5);
    --input-bg: #ffffff;
    --input-text: #141414;
    --input-border: #d1d5db;
    --gray-text: #4b5563;
    --gray-text-secondary: #6b7280;
  }

  .theme-pink {
    --background-color: #fff0f6;
    --text-color: #141414;
    --card-border-color: #f9a8d4;
    --card-text-color: #141414;
    --card-bg: rgba(255, 240, 246, 0.95);
    --glass-bg: rgba(255, 240, 246, 0.5);
    --input-bg: #ffffff;
    --input-text: #141414;
    --input-border: #f9a8d4;
    --gray-text: #4b5563;
    --gray-text-secondary: #6b7280;
  }

  .library-container {
    max-width: 1400px;
    margin: 80px auto 0;
    padding: 24px;
    min-height: calc(100vh - 80px);
    background: var(--background-color);
    color: var(--text-color);
    display: flex;
    flex-direction: column;
    gap: 32px;
    transition: background 0.3s ease;
  }

  .library-header {
    background: var(--glass-bg);
    backdrop-filter: blur(var(--card-blur));
    border-radius: 12px;
    padding: 20px 24px 16px 24px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
  }

  .header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
  }

  .library-title {
    font-size: 24px;
    font-weight: 700;
  }

  .switch-container {
    display: flex;
    gap: 10px;
    justify-content: center;
  }

  .switch-button {
    padding: 10px 20px;
    border-radius: 8px;
    border: none;
    background: var(--glass-bg);
    backdrop-filter: blur(var(--card-blur));
    color: var(--text-color);
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
  }

  .switch-button:hover {
    background: var(--cyber-green);
    color: var(--midnight-black);
  }

  .switch-button.selected {
    background: var(--cyber-green);
    color: var(--midnight-black);
    box-shadow: 0 0 12px var(--cyber-green);
  }

  .age-filter-card {
    background: var(--card-bg);
    border-radius: 8px;
    padding: 16px;
    text-align: center;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    position: relative;
  }

  .age-filter-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: var(--glass-bg);
    backdrop-filter: blur(var(--card-blur));
    z-index: -1;
    border-radius: 8px;
  }

  .age-filter-text {
    font-size: 16px;
    font-weight: 500;
    color: var(--card-text-color);
    margin: 0;
  }

  .library-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 24px;
    padding: 16px 0;
  }

  .library-item {
    background: var(--card-bg);
    border-radius: 8px;
    overflow: hidden;
    transition: all 0.3s ease;
    cursor: pointer;
    position: relative;
    color: var(--card-text-color);
    min-width: 240px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  }

  .library-item::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: var(--glass-bg);
    backdrop-filter: blur(var(--card-blur));
    z-index: -1;
    border-radius: 8px;
  }

  .library-item:hover {
    transform: scale(1.05);
    box-shadow: 0 4px 20px rgba(0, 230, 118, 0.4);
  }

  .library-item:active {
    transform: scale(0.98);
  }

  .thumbnail {
    width: 100%;
    aspect-ratio: 16 / 9;
    object-fit: cover;
    border-radius: 8px 8px 0 0;
  }

  .item-content {
    padding: 12px;
  }

  .item-title {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 8px;
    color: var(--text-color);
  }

  .item-description {
    font-size: 13px;
    color: var(--gray-text-secondary);
    line-height: 1.4;
    margin-bottom: 8px;
  }

  .item-price {
    font-size: 14px;
    font-weight: 600;
    color: var(--cyber-green);
    margin-bottom: 4px;
  }

  .item-points {
    font-size: 14px;
    font-weight: 600;
    color: var(--cosmic-pink);
  }

  .error-message {
    text-align: center;
    padding: 16px;
    color: var(--cosmic-pink);
    font-size: 16px;
    font-weight: 500;
  }

  .skeleton-loader {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 24px;
    padding: 16px 0;
  }

  .skeleton-item {
    background: var(--glass-bg);
    border-radius: 8px;
    height: 300px;
    animation: pulse 1.5s infinite;
  }

  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
  }

  .modal-content {
    background: var(--card-bg);
    border-radius: 12px;
    padding: 24px;
    max-width: 500px;
    width: 90%;
    position: relative;
    animation: slideIn 0.3s ease;
  }

  .modal-close {
    position: absolute;
    top: 10px;
    right: 10px;
    background: none;
    border: none;
    font-size: 18px;
    color: var(--text-color);
    cursor: pointer;
  }

  .modal-thumbnail {
    width: 100%;
    aspect-ratio: 16 / 9;
    object-fit: cover;
    border-radius: 8px;
    margin-bottom: 16px;
  }

  .modal-video {
    width: 100%;
    aspect-ratio: 16 / 9;
    border-radius: 8px;
    margin-bottom: 16px;
    border: none;
  }

  .modal-title {
    font-size: 20px;
    font-weight: 700;
    margin-bottom: 12px;
  }

  .modal-description {
    font-size: 14px;
    color: var(--gray-text-secondary);
    margin-bottom: 16px;
  }

  .modal-price {
    font-size: 16px;
    font-weight: 600;
    color: var(--cyber-green);
    margin-bottom: 8px;
  }

  .modal-points {
    font-size: 16px;
    font-weight: 600;
    color: var(--cosmic-pink);
    margin-bottom: 16px;
  }

  .modal-actions {
    display: flex;
    gap: 10px;
    justify-content: flex-end;
  }

  .modal-action {
    background: var(--cyber-green);
    color: var(--midnight-black);
    border: none;
    border-radius: 8px;
    padding: 10px 20px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
  }

  .modal-action:hover {
    box-shadow: 0 0 12px var(--cyber-green);
  }

  .modal-play {
    background: var(--cyber-green);
    color: var(--midnight-black);
    border: none;
    border-radius: 8px;
    padding: 10px 20px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
  }

  .modal-play:hover {
    box-shadow: 0 0 12px var(--cyber-green);
  }

  @keyframes pulse {
    0% { opacity: 0.6; }
    50% { opacity: 1; }
    100% { opacity: 0.6; }
  }

  @keyframes slideIn {
    from { transform: translateY(-50px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }

  @media (max-width: 768px) {
    .library-container {
      padding: 16px;
      margin-top: 100px;
      min-height: calc(100vh - 60px);
    }

    .library-header {
      flex-direction: column;
      gap: 16px;
      padding: 20px 12px 12px 12px;
    }

    .header-content {
      flex-direction: column;
      gap: 12px;
    }

    .switch-container {
      flex-wrap: wrap;
      justify-content: center;
      gap: 8px;
    }

    .switch-button {
      padding: 8px 16px;
      font-size: 13px;
    }

    .age-filter-card {
      padding: 12px;
    }

    .age-filter-text {
      font-size: 14px;
    }

    .library-grid {
      grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
      gap: 16px;
    }

    .library-item {
      min-width: 180px;
    }

    .skeleton-item {
      min-width: 180px;
      height: 250px;
    }

    .item-title {
      font-size: 14px;
    }

    .item-description {
      font-size: 12px;
    }

    .item-price, .item-points {
      font-size: 12px;
    }

    .modal-content {
      padding: 16px;
    }

    .modal-actions {
      flex-direction: column;
      gap: 8px;
    }
   
    .modal-action, .modal-play {
      width: 100%;
      text-align: center;
    }
  }

  @media (max-width: 480px) {
    .library-grid {
      grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
      gap: 12px;
    }

    .library-item {
      min-width: 140px;
    }

    .skeleton-item {
      min-width: 140px;
      height: 200px;
    }
  }
`;

export default Library;